import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Auth } from "aws-amplify";

// Types
import { ParamsList, Patch, ResultList, User } from "../../types";

// Helpers
import { API_URL } from "../../consts";

export const usersApi = createApi({
  reducerPath: "users",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    async prepareHeaders(headers: Headers): Promise<Headers> {
      const session = await Auth.currentSession();
      const jwtToken = session.getIdToken().getJwtToken();

      headers.set("Authorization", `Bearer ${jwtToken}`);

      return headers;
    },
  }),
  tagTypes: ["Users"],
  endpoints: (builder) => ({
    listUsers: builder.query<ResultList<User>, ParamsList>({
      query: ({ token, limit, filter }) => {
        const encodedToken = token !== undefined && !filter ? encodeURIComponent(token) : "";
        const searchFilter = filter ? `email%5E="${filter}"` : "";
        return `admin/users?token=${encodedToken}&limit=${limit}&filter=${searchFilter}`
      },
      providesTags: () => [{ type: "Users", id: "LIST" }],
    }),

    patchUser: builder.mutation<User, Patch<User, ParamsList>>({
      query: ({ patch }) => {
        return {
        url: `admin/users/${encodeURIComponent(patch.id)}`,
        method: "PATCH",
        body: patch,
      }},
      invalidatesTags: (_result, _error, { patch: { id } }) => [
        { type: "Users" as const, id },
      ],
      async onQueryStarted(
        { patch: { id }, args },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data: patched } = await queryFulfilled;

          dispatch(
            usersApi.util.updateQueryData("listUsers", args, (draft) => {
              for (const [index, user] of draft.payload.entries()) {
                if (user.id === id) {
                  draft.payload[index] = patched;
                }
              }
            })
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),

    deleteUser: builder.mutation<{ message: string }, { userId: string; args: ParamsList }>({
      query: ({ userId }) => ({
        url: `admin/users/${encodeURIComponent(userId)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "Users" as const, id: userId },
        { type: "Users" as const, id: "LIST" },
      ],
      async onQueryStarted(
        { userId, args },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;

          dispatch(
            usersApi.util.updateQueryData("listUsers", args, (draft) => {
              draft.payload = draft.payload.filter(user => user.id !== userId);
            })
          );
        } catch (e) {
          console.error(e);
        }
      },
    }),
  }),
});

export const {
  useListUsersQuery,
  usePatchUserMutation,
  useDeleteUserMutation,
  useLazyListUsersQuery,
} = usersApi;
